import type { Attachment } from "@/api/attachment";
import { useSections } from "@/api/cms";
import { useAuth } from "@/context/auth/AuthContext";
import { getMainLanguage } from "@/i18n";
import type { CatalogueRaisonneSection } from "@/lib/cms/cms-types";
import type { BlockStyle } from "@/lib/cms-puck/block-style";
import { normalizeCaptionAlign } from "@/lib/cms-puck/blocks/selection";
import { useBlockTextCss } from "@/hooks/use-block-style-css";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { useMemo } from "react";

type DecoupageValue = {
  target: "ARTWORKS" | "MULTIPRINTS";
  decoupageId: string | null;
};

type SectionWithMedia = CatalogueRaisonneSection & {
  attachment?: Attachment | null;
};

const NAME_ALIGN_CLASS: Record<string, string> = {
  left: "text-left",
  center: "text-center",
  right: "text-right",
};

function getVignetteDisplayUrl(vignette: Attachment): string | undefined {
  if (vignette.kind === "video" || vignette.kind === "embed") {
    return (
      vignette.posterPublicUrl ??
      vignette.mediumPublicUrl ??
      vignette.publicUrl ??
      vignette.largePublicUrl
    );
  }
  return (
    vignette.mediumPublicUrl ??
    vignette.publicUrl ??
    vignette.largePublicUrl ??
    vignette.smallPublicUrl
  );
}

/**
 * Aperçu canvas du bloc « Découpage » — une carte vignette pour le découpage choisi.
 */
export function CatalogueDecoupagePreview({
  decoupage,
  showSectionName = true,
  nameAlign = "center",
  style,
}: {
  decoupage?: DecoupageValue | null;
  showSectionName?: boolean;
  nameAlign?: string;
  style?: BlockStyle;
}) {
  const vt = useVisibleT();
  const { instanceData } = useAuth();
  const cmsId = instanceData?.CMS?.id ?? "";
  const target = decoupage?.target ?? "ARTWORKS";
  const decoupageId = decoupage?.decoupageId ?? null;
  const mainLang = getMainLanguage();
  const captionTextCss = useBlockTextCss(style);
  const alignClass = NAME_ALIGN_CLASS[normalizeCaptionAlign(nameAlign)];

  const { data: sections = [], isLoading } = useSections(cmsId, target);

  const section = useMemo(
    () => sections.find((s) => s.id === decoupageId),
    [sections, decoupageId],
  );

  if (!decoupageId) {
    return (
      <div className="flex w-full items-center justify-center rounded border border-dashed border-slate-300 py-10 text-sm text-slate-500">
        {vt("cms.new.preview.catalogueDecoupage.pickHint", "Choisissez un découpage dans le picker.")}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="w-full py-6 text-center text-sm cms-page-text-muted">
        {vt("cms.new.preview.catalogueDecoupage.loading", "Chargement…")}
      </div>
    );
  }

  if (!section) {
    return (
      <div className="w-full py-6 text-center text-sm cms-page-text-muted">
        {vt("cms.new.preview.catalogueDecoupage.notFound", "Découpage introuvable.")}
      </div>
    );
  }

  const names = (section.nameMultilingual ?? {}) as Record<string, string>;
  const name =
    names[mainLang] || Object.values(names).find(Boolean) || "";
  // Vignette = celle liée à la série (`vignetteId`), jamais la position
  // `decoupage<TARGET>-<index>` : figée à l'upload, elle ne suit pas le
  // réordonnancement des découpages (parité avec le front public).
  const rawVignette =
    (section as SectionWithMedia).attachment ??
    (section as SectionWithMedia).vignette;
  const url = rawVignette ? getVignetteDisplayUrl(rawVignette) : undefined;

  return (
    <div className="w-full">
      <div className="group block w-full">
        <div className="w-full overflow-hidden bg-slate-100">
          {url ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={url}
              alt={name}
              className="h-auto w-full object-cover transition-transform group-hover:scale-105"
            />
          ) : (
            <div className="flex aspect-[3/2] w-full items-center justify-center text-xs cms-page-text-muted">
              {showSectionName ? name : "—"}
            </div>
          )}
        </div>
        {showSectionName ? (
          <p
            className={`mt-2 text-sm font-medium ${alignClass}`}
            style={captionTextCss}
          >
            {name}
          </p>
        ) : null}
      </div>
    </div>
  );
}
